import React from 'react';
import { X, ExternalLink, Pencil, AlertCircle, CheckCircle2 } from 'lucide-react';
import type { CreativeSlotState } from '../../../types/selfService';
import CreativeUploader from '../shared/CreativeUploader';
import { useCreativePreviewUrl } from '../shared/useCreativePreviewUrl';
import { tintFromAccent } from '../steps/_shared';
import { REJECTION_REASONS_BY_CODE } from '../rejectionReasons';
import Banner from '../shared/Banner';
import { encodeUnsafeUrlChars } from '../../../lib/urlValidation';

interface CreativeSlotCardProps {
  slot: CreativeSlotState;
  // The current station's slug — needed by /creatives/preview-url to resolve
  // a signed URL for a previously-uploaded storagePath.
  slug: string;
  // The picked File for this slot (if any). Null when the slot is empty or
  // when the slot was populated by a prior upload that the user is returning
  // to.
  file: File | null;
  // True when at least one commercial has been uploaded; suppresses the
  // per-slot delete button when this is the only slot.
  removable: boolean;
  index: number;
  accentColor: string;
  // CTV-457: positional palette colour for this slot — kept in sync with the
  // gantt bar that represents the same commercial in the Scheduler.
  color: string;
  uploading?: boolean;
  /** Real 0–100 byte progress while uploading. */
  uploadPercent?: number | null;
  uploadError?: string | null;
  /** True after a successful upload this session. */
  uploadSucceeded?: boolean;
  onFileChange: (file: File | null) => void;
  onClickThroughUrlChange: (next: string) => void;
  onRemove: () => void;
  // CTV-457 follow-up: opens the library picker modal in the parent for this
  // slot. Undefined when the user has no reusable library items (or no seat).
  onPickFromLibrary?: () => void;
  // Set only when this slot currently holds the Waymark AI commercial. Renders
  // a "Customize" pill that reopens the Waymark editor for the same videoId.
  onCustomize?: () => void;
  // CTV-466 follow-up: extra status pill rendered next to the slot header.
  // Used by the Edit Commercial tab to mark slots as "Running" (already
  // approved on the live submission) or "New — awaiting review" (just
  // added in this edit session). Not used by the wizard.
  statusPill?: { label: string; tone: 'neutral' | 'pending' };
}

/**
 * One commercial slot in Step 3. Renders the unified CreativeUploader (no
 * separate "already uploaded" banner) plus a per-slot click-through URL
 * input and remove button. Date + weight controls live in the Scheduler.
 */
const CreativeSlotCard: React.FC<CreativeSlotCardProps> = ({
  slot,
  slug,
  file,
  removable,
  index,
  accentColor,
  color,
  uploading,
  uploadPercent,
  uploadError,
  uploadSucceeded,
  onFileChange,
  onClickThroughUrlChange,
  onRemove,
  onPickFromLibrary,
  onCustomize,
  statusPill,
}) => {
  const storagePath = !file && slot.storagePath ? slot.storagePath : null;
  const { url: previewUrl, loading: previewLoading } = useCreativePreviewUrl(slug, storagePath);
  const isRejected = !!slot.rejected;
  const hasReviewerNotes =
    isRejected &&
    (!!slot.rejectionReason ||
      (Array.isArray(slot.rejectionReasonCodes) && slot.rejectionReasonCodes.length > 0));

  return (
    <div className={`space-y-3 ${isRejected ? 'rounded-xl border border-rose-300 bg-rose-50/30 p-4 -m-1' : ''}`}>
      {/* Slot header — visible when this card is removable. When `index < 0`
          the slot has no creative yet (an empty extra), so we show a neutral
          "New commercial" placeholder instead of a numbered Commercial N. */}
      {(removable || isRejected || statusPill) && (
        <div className="flex items-center gap-2">
          <span
            aria-hidden
            className="inline-block w-2.5 h-2.5 rounded-full shrink-0"
            style={{ backgroundColor: color }}
          />
          <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-600">
            {index >= 0 ? `Commercial ${index + 1}` : 'New commercial'}
          </span>
          {isRejected && (
            <span className="text-[10px] uppercase tracking-[0.08em] px-1.5 py-0.5 rounded-full text-rose-700 bg-rose-100 inline-flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              Rejected
            </span>
          )}
          {!isRejected && statusPill && (
            <span
              className={`text-[10px] uppercase tracking-[0.08em] px-1.5 py-0.5 rounded-full ${
                statusPill.tone === 'pending'
                  ? 'text-emerald-700 bg-emerald-50'
                  : 'text-slate-600 bg-slate-100'
              }`}
            >
              {statusPill.label}
            </span>
          )}
          {/* CTV-476: per-segment shares are surfaced in the Scheduler's
              Weight column and on each gantt segment, not on the slot card. */}
          <div className="flex-1" />
          {removable && (
            <button
              type="button"
              onClick={onRemove}
              className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-500 hover:text-red-700 px-2 py-1 rounded-full hover:bg-red-50"
            >
              <X className="w-3 h-3" />
              Remove
            </button>
          )}
        </div>
      )}
      {/* CTV-466 follow-up: when the slot was carried back into the wizard
          after a publisher rejection, surface the reviewer notes so the
          advertiser knows exactly what to fix. Uploading a replacement file
          clears this state via the slot file-pick handlers. */}
      {hasReviewerNotes && (
        <Banner tone="warn" label="Reviewer notes">
          {Array.isArray(slot.rejectionReasonCodes) && slot.rejectionReasonCodes.length > 0 && (
            <ul className="space-y-0.5 mb-1">
              {slot.rejectionReasonCodes.map((code) => {
                const r = REJECTION_REASONS_BY_CODE[code];
                if (!r) return null;
                return (
                  <li key={code} className="text-rose-800">
                    <span className="font-medium text-rose-900">{r.label}</span>
                    {r.advertiserText && (
                      <>
                        {' — '}
                        <span className="text-rose-700/90">{r.advertiserText}</span>
                      </>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          {slot.rejectionReason && (
            <div className="pl-3 border-l-2 border-rose-200 text-rose-800 leading-relaxed italic">
              "{slot.rejectionReason}"
            </div>
          )}
          <p className="text-[11px] text-rose-700/80 mt-1">
            Upload a replacement video below — or remove this slot — to continue.
          </p>
        </Banner>
      )}

      <CreativeUploader
        file={file}
        onFileChange={onFileChange}
        accentColor={accentColor}
        existingVideoUrl={previewUrl}
        existingMeta={slot.storagePath ? { filename: slot.filename, sizeBytes: slot.sizeBytes } : null}
        resolvingExistingVideo={previewLoading}
        uploading={!!uploading && !uploadError}
        uploadPercent={uploadPercent}
        onPickFromLibrary={onPickFromLibrary}
      />
      {uploadError && (
        <Banner tone="warn" label="Upload failed">
          {uploadError}. Pick the file again to retry.
        </Banner>
      )}
      {uploadSucceeded && !uploadError && !uploading && (
        <Banner
          tone="success"
          label={slot.waymarkCommercialId ? 'AI commercial added' : 'Upload completed'}
          icon={<CheckCircle2 className="w-3 h-3" />}
          compact
        />
      )}
      {onCustomize && (
        <button
          type="button"
          onClick={onCustomize}
          className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] px-2.5 py-1 rounded-full border border-slate-200 text-slate-500 hover:text-slate-900 hover:bg-slate-50 transition-all"
        >
          <Pencil className="w-3.5 h-3.5" />
          Review this AI commercial
        </button>
      )}

      {/* Per-slot click-through URL. */}
      <label className="block">
        <span className="text-[10px] font-medium uppercase tracking-[0.1em] text-slate-400 flex items-center gap-1.5">
          <ExternalLink className="w-3 h-3" /> Click-through URL (optional)
        </span>
        <input
          type="text"
          value={slot.clickThroughUrl}
          onChange={(e) => onClickThroughUrlChange(e.target.value)}
          // Percent-encode " ' < > on blur — never legitimate unencoded in a URL
          // and unsafe once this reaches Publica's ClickThroughURL. `&` is left
          // intact: UTM parameters need it and Publica accepts it here.
          onBlur={(e) => {
            const encoded = encodeUnsafeUrlChars(e.target.value.trim());
            if (encoded !== e.target.value) onClickThroughUrlChange(encoded);
          }}
          placeholder="https://www.yourbusiness.com/offer"
          className="mt-1 w-full text-[13px] tracking-tight text-slate-900 bg-white border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2"
          style={{ '--tw-ring-color': tintFromAccent(accentColor, 0.4) } as React.CSSProperties}
        />
      </label>
    </div>
  );
};

export default CreativeSlotCard;
